import {
  groupItemsByStation,
  KITCHEN_ROUTING_CACHE_SECTION,
  type KitchenRoutingEntry,
} from "./kitchen-routing";
import type {
  CompleteLocalOrderInput,
  CreateLocalOrderInput,
  LocalCustomerDraftRecord,
  LocalKitchenTicketItemRecord,
  LocalKitchenTicketOperationRecord,
  LocalKitchenTicketRecord,
  LocalKitchenTicketStatus,
  LocalKitchenTicketWithDetails,
  LocalOrderCustomerSnapshot,
  LocalOrderOperationRecord,
  LocalOrderOperationType,
  LocalOrderRecord,
  MergeLocalOrdersInput,
  MoveLocalOrderTableInput,
  OfflineOrderRecord,
  OfflineSyncSummary,
  ReferenceCacheRecord,
  ReferenceCacheStaleness,
  RefundLocalOrderInput,
  UpdateLocalOrderInput,
  VoidLocalOrderInput,
} from "./types";

const DB_NAME = "cafe-pos-offline-sync";
const DB_VERSION = 5;
const OFFLINE_ORDERS_STORE = "offline_orders";
const LOCAL_ORDERS_STORE = "local_orders";
const LOCAL_ORDER_OPERATIONS_STORE = "local_order_operations";
const LOCAL_CUSTOMER_DRAFTS_STORE = "local_customer_drafts";
const LOCAL_KITCHEN_TICKETS_STORE = "local_kitchen_tickets";
const LOCAL_KITCHEN_TICKET_ITEMS_STORE = "local_kitchen_ticket_items";
const LOCAL_KITCHEN_TICKET_OPERATIONS_STORE = "local_kitchen_ticket_operations";
const SYNC_JOBS_STORE = "sync_jobs";
const REFERENCE_CACHE_STORE = "reference_cache";
const SYNC_LOGS_STORE = "sync_logs";

export const OFFLINE_SYNC_CHANGED_EVENT = "pos-offline-sync-changed";

function assertIndexedDbAvailable(): IDBFactory {
  if (!globalThis.indexedDB) {
    throw new Error("IndexedDB is not available in this browser");
  }
  return globalThis.indexedDB;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function ensureIndex(
  store: IDBObjectStore,
  name: string,
  keyPath: string | string[],
  options?: IDBIndexParameters,
) {
  if (!store.indexNames.contains(name)) {
    store.createIndex(name, keyPath, options);
  }
}

function openOfflineSyncDb(): Promise<IDBDatabase> {
  const indexedDb = assertIndexedDbAvailable();

  return new Promise((resolve, reject) => {
    const request = indexedDb.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(OFFLINE_ORDERS_STORE)) {
        const orders = db.createObjectStore(OFFLINE_ORDERS_STORE, {
          keyPath: "clientOrderId",
        });
        orders.createIndex("by_status", "status", { unique: false });
        orders.createIndex("by_shift", "shiftId", { unique: false });
        orders.createIndex("by_branch_device", ["branchId", "deviceId"], {
          unique: false,
        });
        orders.createIndex("by_created_at", "createdAt", { unique: false });
      }

      if (!db.objectStoreNames.contains(LOCAL_ORDERS_STORE)) {
        const localOrders = db.createObjectStore(LOCAL_ORDERS_STORE, {
          keyPath: "clientOrderId",
        });
        localOrders.createIndex("by_status", "status", { unique: false });
        localOrders.createIndex("by_sync_status", "syncStatus", {
          unique: false,
        });
        localOrders.createIndex("by_shift", "shiftId", { unique: false });
        localOrders.createIndex("by_branch_device", ["branchId", "deviceId"], {
          unique: false,
        });
        localOrders.createIndex("by_shift_sync_status", [
          "shiftId",
          "syncStatus",
        ]);
        localOrders.createIndex("by_created_at", "createdAt", {
          unique: false,
        });
      }

      if (db.objectStoreNames.contains(LOCAL_ORDERS_STORE)) {
        const localOrders =
          request.transaction?.objectStore(LOCAL_ORDERS_STORE);
        if (localOrders) {
          ensureIndex(
            localOrders,
            "by_merged_into",
            "mergedIntoClientOrderId",
            {
              unique: false,
            },
          );
        }
      }

      if (!db.objectStoreNames.contains(LOCAL_CUSTOMER_DRAFTS_STORE)) {
        const customerDrafts = db.createObjectStore(
          LOCAL_CUSTOMER_DRAFTS_STORE,
          {
            keyPath: "localCustomerId",
          },
        );
        customerDrafts.createIndex("by_phone", "phone", { unique: false });
        customerDrafts.createIndex("by_sync_status", "syncStatus", {
          unique: false,
        });
        customerDrafts.createIndex("by_branch", "branchId", { unique: false });
        customerDrafts.createIndex("by_branch_phone", ["branchId", "phone"], {
          unique: false,
        });
      }

      if (db.objectStoreNames.contains(LOCAL_CUSTOMER_DRAFTS_STORE)) {
        const customerDrafts = request.transaction?.objectStore(
          LOCAL_CUSTOMER_DRAFTS_STORE,
        );
        if (customerDrafts) {
          ensureIndex(customerDrafts, "by_phone", "phone", { unique: false });
          ensureIndex(customerDrafts, "by_sync_status", "syncStatus", {
            unique: false,
          });
          ensureIndex(customerDrafts, "by_branch", "branchId", {
            unique: false,
          });
          ensureIndex(
            customerDrafts,
            "by_branch_phone",
            ["branchId", "phone"],
            { unique: false },
          );
        }
      }

      if (!db.objectStoreNames.contains(LOCAL_ORDER_OPERATIONS_STORE)) {
        const operations = db.createObjectStore(LOCAL_ORDER_OPERATIONS_STORE, {
          keyPath: "id",
          autoIncrement: true,
        });
        operations.createIndex("by_client_order", "clientOrderId", {
          unique: false,
        });
        operations.createIndex("by_operation_id", "operationId", {
          unique: true,
        });
        operations.createIndex("by_shift", "shiftId", { unique: false });
        operations.createIndex("by_created_at", "createdAt", {
          unique: false,
        });
      }

      if (db.objectStoreNames.contains(LOCAL_ORDER_OPERATIONS_STORE)) {
        const operations = request.transaction?.objectStore(
          LOCAL_ORDER_OPERATIONS_STORE,
        );
        if (operations) {
          ensureIndex(operations, "by_type", "type", { unique: false });
          ensureIndex(operations, "by_sync_status", "syncStatus", {
            unique: false,
          });
        }
      }

      if (!db.objectStoreNames.contains(LOCAL_KITCHEN_TICKETS_STORE)) {
        const tickets = db.createObjectStore(LOCAL_KITCHEN_TICKETS_STORE, {
          keyPath: "localTicketId",
        });
        tickets.createIndex("by_client_order", "clientOrderId", {
          unique: false,
        });
        tickets.createIndex("by_branch", "branchId", { unique: false });
        tickets.createIndex("by_shift", "shiftId", { unique: false });
        tickets.createIndex("by_status", "status", { unique: false });
        tickets.createIndex("by_queued_at", "queuedAt", { unique: false });
      }

      if (!db.objectStoreNames.contains(LOCAL_KITCHEN_TICKET_ITEMS_STORE)) {
        const items = db.createObjectStore(LOCAL_KITCHEN_TICKET_ITEMS_STORE, {
          keyPath: "localTicketItemId",
        });
        items.createIndex("by_ticket", "localTicketId", { unique: false });
        items.createIndex("by_client_order", "clientOrderId", {
          unique: false,
        });
      }

      if (
        !db.objectStoreNames.contains(LOCAL_KITCHEN_TICKET_OPERATIONS_STORE)
      ) {
        const operations = db.createObjectStore(
          LOCAL_KITCHEN_TICKET_OPERATIONS_STORE,
          {
            keyPath: "id",
            autoIncrement: true,
          },
        );
        operations.createIndex("by_ticket", "localTicketId", {
          unique: false,
        });
        operations.createIndex("by_client_order", "clientOrderId", {
          unique: false,
        });
        operations.createIndex("by_created_at", "createdAt", {
          unique: false,
        });
      }

      if (!db.objectStoreNames.contains(SYNC_JOBS_STORE)) {
        db.createObjectStore(SYNC_JOBS_STORE, { keyPath: "jobId" });
      }

      if (!db.objectStoreNames.contains(REFERENCE_CACHE_STORE)) {
        db.createObjectStore(REFERENCE_CACHE_STORE, { keyPath: "key" });
      }

      if (!db.objectStoreNames.contains(SYNC_LOGS_STORE)) {
        const logs = db.createObjectStore(SYNC_LOGS_STORE, {
          keyPath: "id",
          autoIncrement: true,
        });
        logs.createIndex("by_created_at", "createdAt", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function notifyOfflineSyncChanged() {
  window.dispatchEvent(new CustomEvent(OFFLINE_SYNC_CHANGED_EVENT));
}

function createLocalId(prefix: string): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid
    ? `${prefix}_${uuid}`
    : `${prefix}_${Date.now()}_${Math.random()}`;
}

function appendLocalOrderOperation(
  store: IDBObjectStore,
  input: {
    type: LocalOrderOperationType;
    order: Pick<
      LocalOrderRecord,
      "clientOrderId" | "branchId" | "deviceId" | "shiftId"
    >;
    syncStatus?: LocalOrderOperationRecord["syncStatus"];
    capturedAt?: string;
    performedBy?: LocalOrderOperationRecord["performedBy"];
    payload?: unknown;
    createdAt: string;
  },
) {
  const record: LocalOrderOperationRecord = {
    operationId: createLocalId("local_op"),
    clientOrderId: input.order.clientOrderId,
    branchId: input.order.branchId,
    deviceId: input.order.deviceId,
    shiftId: input.order.shiftId,
    type: input.type,
    syncStatus: input.syncStatus ?? "not_ready",
    capturedAt: input.capturedAt ?? input.createdAt,
    performedBy: input.performedBy,
    payload: input.payload,
    retryCount: 0,
    createdAt: input.createdAt,
  };
  store.add(record);
}

function appendLocalKitchenTicketOperation(
  store: IDBObjectStore,
  input: {
    localTicketId: string;
    clientOrderId: string;
    branchId: string;
    type: LocalKitchenTicketOperationRecord["type"];
    status?: LocalKitchenTicketStatus;
    payload?: unknown;
    createdAt: string;
  },
) {
  const record: LocalKitchenTicketOperationRecord = {
    operationId: createLocalId("local_kds_op"),
    localTicketId: input.localTicketId,
    clientOrderId: input.clientOrderId,
    branchId: input.branchId,
    type: input.type,
    status: input.status,
    payload: input.payload,
    createdAt: input.createdAt,
  };
  store.add(record);
}

function getLocalOrderType(
  order: Pick<LocalOrderRecord, "isDelivery" | "selection">,
): "DINE_IN" | "TAKEAWAY" | "DELIVERY" {
  if (order.isDelivery) return "DELIVERY";
  if (order.selection.tableId) return "DINE_IN";
  return "TAKEAWAY";
}

/**
 * Reads cached kitchen routing for a branch directly from the reference
 * cache store. Kept local to avoid a circular import with reference-cache.
 * Returns [] when nothing is cached so callers fall back to a single
 * default "Kitchen" ticket.
 */
async function getCachedKitchenRoutingEntries(
  branchId: string,
): Promise<KitchenRoutingEntry[]> {
  const key = `branch:${branchId}:${KITCHEN_ROUTING_CACHE_SECTION}`;
  const record = await getReferenceCache<KitchenRoutingEntry[]>(key);
  return Array.isArray(record?.data) ? record.data : [];
}

function appendLocalKitchenTicketsForOrder(
  ticketsStore: IDBObjectStore,
  itemsStore: IDBObjectStore,
  operationsStore: IDBObjectStore,
  order: LocalOrderRecord,
  createdAt: string,
  cartItems: LocalOrderRecord["cartItems"] = order.cartItems,
  baseTicketSequence = 1,
  routingEntries: KitchenRoutingEntry[] = [],
) {
  if (cartItems.length === 0) return;

  const stationGroups = groupItemsByStation(cartItems, routingEntries);

  stationGroups.forEach((group, groupIndex) => {
    const localTicketId = createLocalId("local_kitchen_ticket");
    const ticket: LocalKitchenTicketRecord = {
      localTicketId,
      clientOrderId: order.clientOrderId,
      clientOrderNo: order.clientOrderNo,
      branchId: order.branchId,
      deviceId: order.deviceId,
      shiftId: order.shiftId,
      stationId: group.stationId,
      stationName: group.stationName,
      stationCode: group.stationCode,
      status: "QUEUED",
      ticketSequence: baseTicketSequence + groupIndex,
      orderType: getLocalOrderType(order),
      tableId: order.selection.tableId ?? null,
      tableName: order.selection.tableName ?? null,
      customerId: order.customerId ?? null,
      queuedAt: createdAt,
      updatedAt: createdAt,
    };

    ticketsStore.add(ticket);
    for (const cartItem of group.items) {
      const item: LocalKitchenTicketItemRecord = {
        localTicketItemId: createLocalId("local_kitchen_item"),
        localTicketId,
        clientOrderId: order.clientOrderId,
        branchId: order.branchId,
        productId: cartItem.productId,
        variantId: cartItem.variantId,
        displayName: cartItem.variantName
          ? `${cartItem.productName} (${cartItem.variantName})`
          : cartItem.productName,
        quantity: cartItem.quantity,
        note: cartItem.note,
        modifiers: cartItem.modifiers.map((modifier) => ({
          id: `${cartItem.id}:${modifier.modifierId}:${modifier.optionValue}`,
          label: modifier.modifierName,
          optionLabel: modifier.optionLabel,
          priceDelta: modifier.priceDelta,
        })),
        createdAt,
      };
      itemsStore.add(item);
    }

    appendLocalKitchenTicketOperation(operationsStore, {
      localTicketId,
      clientOrderId: order.clientOrderId,
      branchId: order.branchId,
      type: "create_ticket",
      status: "QUEUED",
      payload: { itemCount: group.items.length, stationId: group.stationId },
      createdAt,
    });
  });
}

async function nextClientOrderNo(
  store: IDBObjectStore,
  shiftId: string,
): Promise<number> {
  const all = await requestToPromise<LocalOrderRecord[]>(store.getAll());
  const maxOrderNo = all.reduce((max, order) => {
    if (order.shiftId !== shiftId) return max;
    return Math.max(max, order.clientOrderNo);
  }, 0);
  return maxOrderNo + 1;
}

export async function saveOfflineOrder(
  record: OfflineOrderRecord,
): Promise<void> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(OFFLINE_ORDERS_STORE, "readwrite");
    tx.objectStore(OFFLINE_ORDERS_STORE).put(record);
    await transactionDone(tx);
    notifyOfflineSyncChanged();
  } finally {
    db.close();
  }
}

export async function updateOfflineOrderStatus(
  clientOrderId: string,
  updates: Partial<
    Pick<
      OfflineOrderRecord,
      "status" | "errorMessage" | "retryCount" | "updatedAt" | "syncedAt"
    >
  >,
): Promise<void> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(OFFLINE_ORDERS_STORE, "readwrite");
    const store = tx.objectStore(OFFLINE_ORDERS_STORE);
    const existing = await requestToPromise<OfflineOrderRecord | undefined>(
      store.get(clientOrderId),
    );
    if (!existing) return;
    store.put({
      ...existing,
      ...updates,
      updatedAt: updates.updatedAt ?? new Date().toISOString(),
    });
    await transactionDone(tx);
    notifyOfflineSyncChanged();
  } finally {
    db.close();
  }
}

export async function getOfflineOrder(
  clientOrderId: string,
): Promise<OfflineOrderRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(OFFLINE_ORDERS_STORE, "readonly");
    return await requestToPromise<OfflineOrderRecord | undefined>(
      tx.objectStore(OFFLINE_ORDERS_STORE).get(clientOrderId),
    );
  } finally {
    db.close();
  }
}

export async function listOfflineOrdersByStatus(
  statuses: OfflineOrderRecord["status"][],
): Promise<OfflineOrderRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(OFFLINE_ORDERS_STORE, "readonly");
    const store = tx.objectStore(OFFLINE_ORDERS_STORE);
    const all = await requestToPromise<OfflineOrderRecord[]>(store.getAll());
    return all
      .filter((record) => statuses.includes(record.status))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } finally {
    db.close();
  }
}

export async function listOfflineOrdersForShift(
  shiftId: string,
  statuses: OfflineOrderRecord["status"][],
): Promise<OfflineOrderRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(OFFLINE_ORDERS_STORE, "readonly");
    const store = tx.objectStore(OFFLINE_ORDERS_STORE);
    const all = await requestToPromise<OfflineOrderRecord[]>(store.getAll());
    return all
      .filter(
        (record) =>
          record.shiftId === shiftId && statuses.includes(record.status),
      )
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } finally {
    db.close();
  }
}

export async function getOfflineSyncSummary(): Promise<OfflineSyncSummary> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [OFFLINE_ORDERS_STORE, LOCAL_ORDERS_STORE],
      "readonly",
    );
    const offlineRecords = await requestToPromise<OfflineOrderRecord[]>(
      tx.objectStore(OFFLINE_ORDERS_STORE).getAll(),
    );
    const localRecords = await requestToPromise<LocalOrderRecord[]>(
      tx.objectStore(LOCAL_ORDERS_STORE).getAll(),
    );
    const summary = offlineRecords.reduce<OfflineSyncSummary>(
      (acc, record) => {
        if (record.status === "queued") acc.queued += 1;
        if (record.status === "syncing") acc.syncing += 1;
        if (record.status === "failed") acc.failed += 1;
        if (record.status === "needs_review") acc.needsReview += 1;
        return acc;
      },
      { queued: 0, syncing: 0, failed: 0, needsReview: 0, pending: 0 },
    );
    for (const record of localRecords) {
      if (record.syncStatus === "queued") summary.queued += 1;
      if (record.syncStatus === "syncing") summary.syncing += 1;
      if (record.syncStatus === "failed") summary.failed += 1;
      if (record.syncStatus === "needs_review") summary.needsReview += 1;
    }
    summary.pending =
      summary.queued + summary.syncing + summary.failed + summary.needsReview;
    return summary;
  } finally {
    db.close();
  }
}

export async function createLocalOrder(
  input: CreateLocalOrderInput,
): Promise<LocalOrderRecord> {
  const routingEntries = await getCachedKitchenRoutingEntries(input.branchId);
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [
        LOCAL_ORDERS_STORE,
        LOCAL_ORDER_OPERATIONS_STORE,
        LOCAL_KITCHEN_TICKETS_STORE,
        LOCAL_KITCHEN_TICKET_ITEMS_STORE,
        LOCAL_KITCHEN_TICKET_OPERATIONS_STORE,
      ],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const kitchenTickets = tx.objectStore(LOCAL_KITCHEN_TICKETS_STORE);
    const kitchenTicketItems = tx.objectStore(LOCAL_KITCHEN_TICKET_ITEMS_STORE);
    const kitchenTicketOperations = tx.objectStore(
      LOCAL_KITCHEN_TICKET_OPERATIONS_STORE,
    );
    const now = new Date().toISOString();
    const record: LocalOrderRecord = {
      clientOrderId: createLocalId("local_order"),
      clientOrderNo: await nextClientOrderNo(localOrders, input.shiftId),
      idempotencyKey: createLocalId("idem"),
      branchId: input.branchId,
      deviceId: input.deviceId,
      shiftId: input.shiftId,
      status: "OPEN",
      syncStatus: "not_ready",
      selection: input.selection,
      cartItems: input.cartItems,
      isDelivery: input.isDelivery,
      deliveryData: input.deliveryData,
      customerId: input.customerId,
      customerSnapshot: input.customerSnapshot,
      customerSyncStatus: input.customerSyncStatus,
      couponCode: input.couponCode,
      couponApplyToCartIndices: input.couponApplyToCartIndices,
      couponApplications: input.couponApplications,
      couponSnapshots: input.couponSnapshots,
      voucherSnapshots: input.voucherSnapshots,
      rewardSnapshot: input.rewardSnapshot,
      rewardId: input.rewardId ?? input.rewardSnapshot?.rewardId,
      discountBreakdown: input.discountBreakdown,
      pricingSource: input.pricingSource,
      payments: [],
      totals: input.totals,
      retryCount: 0,
      createdAt: now,
      updatedAt: now,
    };

    localOrders.add(record);
    appendLocalOrderOperation(operations, {
      type: "create_order",
      order: record,
      payload: input,
      createdAt: now,
    });
    appendLocalKitchenTicketsForOrder(
      kitchenTickets,
      kitchenTicketItems,
      kitchenTicketOperations,
      record,
      now,
      record.cartItems,
      1,
      routingEntries,
    );
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return record;
  } finally {
    db.close();
  }
}

export async function getLocalOrder(
  clientOrderId: string,
): Promise<LocalOrderRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_ORDERS_STORE, "readonly");
    return await requestToPromise<LocalOrderRecord | undefined>(
      tx.objectStore(LOCAL_ORDERS_STORE).get(clientOrderId),
    );
  } finally {
    db.close();
  }
}

function normalizeCustomerPhone(phone: string): string {
  return phone.trim().replace(/\s+/g, "");
}

export async function saveLocalCustomerDraft(input: {
  branchId: string;
  deviceId?: string;
  phone: string;
  firstName?: string;
  lastName?: string;
  capturedAt?: string;
}): Promise<LocalCustomerDraftRecord> {
  const phone = normalizeCustomerPhone(input.phone);
  if (!phone) {
    throw new Error("Customer phone is required");
  }

  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_CUSTOMER_DRAFTS_STORE, "readwrite");
    const store = tx.objectStore(LOCAL_CUSTOMER_DRAFTS_STORE);
    const existingRows = await requestToPromise<LocalCustomerDraftRecord[]>(
      store
        .index("by_branch_phone")
        .getAll([input.branchId, phone]) as IDBRequest<
        LocalCustomerDraftRecord[]
      >,
    );
    const existing = existingRows.sort((a, b) =>
      b.updatedAt.localeCompare(a.updatedAt),
    )[0];
    const now = new Date().toISOString();

    const record: LocalCustomerDraftRecord = {
      localCustomerId: existing?.localCustomerId ?? createLocalId("local_cust"),
      branchId: input.branchId,
      deviceId: input.deviceId ?? existing?.deviceId,
      phone,
      firstName: input.firstName ?? existing?.firstName,
      lastName: input.lastName ?? existing?.lastName,
      customerSnapshot: {
        localCustomerId:
          existing?.customerSnapshot.localCustomerId ??
          existing?.localCustomerId ??
          createLocalId("local_cust_ref"),
        phone,
        firstName: input.firstName ?? existing?.firstName,
        lastName: input.lastName ?? existing?.lastName,
        source: "local_draft",
        capturedAt:
          input.capturedAt ?? existing?.customerSnapshot.capturedAt ?? now,
      },
      syncStatus:
        existing?.syncStatus === "synced"
          ? "queued"
          : (existing?.syncStatus ?? "queued"),
      serverCustomerId: existing?.serverCustomerId,
      lastSyncError: undefined,
      retryCount: existing?.retryCount ?? 0,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      syncedAt: existing?.syncedAt,
    };

    record.customerSnapshot.localCustomerId = record.localCustomerId;
    store.put(record);
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return record;
  } finally {
    db.close();
  }
}

export async function findLocalCustomerByPhone(
  branchId: string,
  phoneInput: string,
): Promise<LocalCustomerDraftRecord | undefined> {
  const phone = normalizeCustomerPhone(phoneInput);
  if (!phone) return undefined;

  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_CUSTOMER_DRAFTS_STORE, "readonly");
    const rows = await requestToPromise<LocalCustomerDraftRecord[]>(
      tx
        .objectStore(LOCAL_CUSTOMER_DRAFTS_STORE)
        .index("by_branch_phone")
        .getAll([branchId, phone]) as IDBRequest<LocalCustomerDraftRecord[]>,
    );
    return rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  } finally {
    db.close();
  }
}

export async function markLocalCustomerDraftSynced(
  localCustomerId: string,
  serverCustomerId: string,
): Promise<LocalCustomerDraftRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_CUSTOMER_DRAFTS_STORE, "readwrite");
    const store = tx.objectStore(LOCAL_CUSTOMER_DRAFTS_STORE);
    const existing = await requestToPromise<
      LocalCustomerDraftRecord | undefined
    >(store.get(localCustomerId));
    if (!existing) return undefined;

    const now = new Date().toISOString();
    const updated: LocalCustomerDraftRecord = {
      ...existing,
      syncStatus: "synced",
      serverCustomerId,
      customerSnapshot: {
        ...existing.customerSnapshot,
        customerId: serverCustomerId,
        source: "server",
      },
      lastSyncError: undefined,
      updatedAt: now,
      syncedAt: now,
    };
    store.put(updated);
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function markLocalCustomerDraftSyncStatus(
  localCustomerId: string,
  updates: Partial<
    Pick<
      LocalCustomerDraftRecord,
      | "syncStatus"
      | "serverCustomerId"
      | "lastSyncError"
      | "retryCount"
      | "syncedAt"
    >
  >,
): Promise<LocalCustomerDraftRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_CUSTOMER_DRAFTS_STORE, "readwrite");
    const store = tx.objectStore(LOCAL_CUSTOMER_DRAFTS_STORE);
    const existing = await requestToPromise<
      LocalCustomerDraftRecord | undefined
    >(store.get(localCustomerId));
    if (!existing) return undefined;

    const now = new Date().toISOString();
    const updated: LocalCustomerDraftRecord = {
      ...existing,
      ...updates,
      customerSnapshot: {
        ...existing.customerSnapshot,
        customerId:
          updates.serverCustomerId ?? existing.customerSnapshot.customerId,
        source: updates.serverCustomerId
          ? "server"
          : existing.customerSnapshot.source,
      },
      updatedAt: now,
    };
    store.put(updated);
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function listLocalCustomerDraftsForBranch(
  branchId: string,
  options?: {
    syncStatuses?: LocalCustomerDraftRecord["syncStatus"][];
  },
): Promise<LocalCustomerDraftRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_CUSTOMER_DRAFTS_STORE, "readonly");
    const rows = await requestToPromise<LocalCustomerDraftRecord[]>(
      tx
        .objectStore(LOCAL_CUSTOMER_DRAFTS_STORE)
        .index("by_branch")
        .getAll(branchId) as IDBRequest<LocalCustomerDraftRecord[]>,
    );
    await transactionDone(tx);
    const syncStatuses = options?.syncStatuses;
    const filtered = syncStatuses?.length
      ? rows.filter((row) => syncStatuses.includes(row.syncStatus))
      : rows;
    return filtered.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } finally {
    db.close();
  }
}

export async function listUnsyncedLocalCustomerDrafts(
  branchId: string,
): Promise<LocalCustomerDraftRecord[]> {
  return listLocalCustomerDraftsForBranch(branchId, {
    syncStatuses: ["queued", "syncing", "failed", "needs_review"],
  });
}

export async function retryLocalCustomerDraftSync(
  localCustomerId: string,
): Promise<LocalCustomerDraftRecord | undefined> {
  return markLocalCustomerDraftSyncStatus(localCustomerId, {
    syncStatus: "queued",
    lastSyncError: undefined,
  });
}

export async function replaceLocalOrderCustomerMapping(
  clientOrderId: string,
  input: {
    phone: string;
    firstName?: string;
    lastName?: string;
    customerId?: string;
    source?: LocalOrderCustomerSnapshot["source"];
  },
): Promise<LocalOrderRecord | undefined> {
  const phone = normalizeCustomerPhone(input.phone);
  if (!phone) {
    throw new Error("Customer phone is required");
  }

  const order = await getLocalOrder(clientOrderId);
  if (!order) return undefined;

  const now = new Date().toISOString();
  const customerSnapshot: LocalOrderCustomerSnapshot = {
    customerId: input.customerId ?? order.customerSnapshot?.customerId,
    localCustomerId: order.customerSnapshot?.localCustomerId,
    phone,
    firstName: input.firstName ?? order.customerSnapshot?.firstName,
    lastName: input.lastName ?? order.customerSnapshot?.lastName,
    source: input.source ?? (input.customerId ? "server" : "cache"),
    capturedAt: now,
  };

  return updateLocalOrderCustomerSnapshot(clientOrderId, {
    customerId: input.customerId ?? order.customerId,
    customerSnapshot,
    customerSyncStatus: input.customerId ? "synced" : "queued",
  });
}

export async function updateLocalOrderCustomerSnapshot(
  clientOrderId: string,
  updates: Partial<
    Pick<
      LocalOrderRecord,
      "customerId" | "customerSnapshot" | "customerSyncStatus"
    >
  >,
): Promise<LocalOrderRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_ORDERS_STORE, LOCAL_ORDER_OPERATIONS_STORE],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const existing = await requestToPromise<LocalOrderRecord | undefined>(
      localOrders.get(clientOrderId),
    );
    if (!existing) return undefined;

    const now = new Date().toISOString();
    const updated: LocalOrderRecord = {
      ...existing,
      ...updates,
      updatedAt: now,
    };
    localOrders.put(updated);
    appendLocalOrderOperation(operations, {
      type: "update_order",
      order: updated,
      payload: {
        reason: "customer_snapshot_updated",
        ...updates,
      },
      createdAt: now,
    });
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function updateLocalOrderPricingSnapshot(
  clientOrderId: string,
  updates: Partial<
    Pick<
      LocalOrderRecord,
      | "couponCode"
      | "couponApplyToCartIndices"
      | "couponApplications"
      | "couponSnapshots"
      | "voucherCodes"
      | "voucherSnapshots"
      | "discountBreakdown"
      | "pricingSource"
      | "totals"
    >
  >,
): Promise<LocalOrderRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_ORDERS_STORE, LOCAL_ORDER_OPERATIONS_STORE],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const existing = await requestToPromise<LocalOrderRecord | undefined>(
      localOrders.get(clientOrderId),
    );
    if (!existing) return undefined;

    const now = new Date().toISOString();
    const updated: LocalOrderRecord = {
      ...existing,
      ...updates,
      updatedAt: now,
    };
    localOrders.put(updated);
    appendLocalOrderOperation(operations, {
      type: "update_order",
      order: updated,
      payload: {
        reason: "pricing_snapshot_updated",
        ...updates,
      },
      createdAt: now,
    });
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function listLocalOrdersForShift(
  shiftId: string,
): Promise<LocalOrderRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_ORDERS_STORE, "readonly");
    const all = await requestToPromise<LocalOrderRecord[]>(
      tx.objectStore(LOCAL_ORDERS_STORE).getAll(),
    );
    return all
      .filter((record) => record.shiftId === shiftId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } finally {
    db.close();
  }
}

/**
 * Returns every locally-stored order for a branch regardless of shift.
 * Used as a recovery fallback in the Orders page when the active shift context
 * cannot be resolved (e.g. offline right after a device reboot) so previously
 * saved offline orders are never hidden.
 */
export async function listLocalOrdersForBranch(
  branchId: string,
): Promise<LocalOrderRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_ORDERS_STORE, "readonly");
    const all = await requestToPromise<LocalOrderRecord[]>(
      tx.objectStore(LOCAL_ORDERS_STORE).getAll(),
    );
    return all
      .filter((record) => record.branchId === branchId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } finally {
    db.close();
  }
}

export async function listOpenLocalOrdersForBranch(
  branchId: string,
): Promise<LocalOrderRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_ORDERS_STORE, "readonly");
    const all = await requestToPromise<LocalOrderRecord[]>(
      tx.objectStore(LOCAL_ORDERS_STORE).getAll(),
    );
    return all
      .filter(
        (record) => record.branchId === branchId && record.status === "OPEN",
      )
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } finally {
    db.close();
  }
}

export async function listLocalKitchenTicketsForBranch(
  branchId: string,
  filters: {
    shiftId?: string;
    stationId?: string;
    status?: LocalKitchenTicketStatus;
  } = {},
): Promise<LocalKitchenTicketWithDetails[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_KITCHEN_TICKETS_STORE, LOCAL_KITCHEN_TICKET_ITEMS_STORE],
      "readonly",
    );
    const tickets = await requestToPromise<LocalKitchenTicketRecord[]>(
      tx.objectStore(LOCAL_KITCHEN_TICKETS_STORE).getAll(),
    );
    const items = await requestToPromise<LocalKitchenTicketItemRecord[]>(
      tx.objectStore(LOCAL_KITCHEN_TICKET_ITEMS_STORE).getAll(),
    );
    const itemsByTicket = new Map<string, LocalKitchenTicketItemRecord[]>();
    for (const item of items) {
      const current = itemsByTicket.get(item.localTicketId) ?? [];
      current.push(item);
      itemsByTicket.set(item.localTicketId, current);
    }

    return tickets
      .filter((ticket) => ticket.branchId === branchId)
      .filter(
        (ticket) => !filters.shiftId || ticket.shiftId === filters.shiftId,
      )
      .filter(
        (ticket) =>
          !filters.stationId || ticket.stationId === filters.stationId,
      )
      .filter((ticket) => !filters.status || ticket.status === filters.status)
      .map((ticket) => ({
        ticket,
        items: itemsByTicket.get(ticket.localTicketId) ?? [],
      }))
      .sort((a, b) => b.ticket.queuedAt.localeCompare(a.ticket.queuedAt));
  } finally {
    db.close();
  }
}

export async function updateLocalKitchenTicketStatus(
  localTicketId: string,
  status: LocalKitchenTicketStatus,
): Promise<LocalKitchenTicketRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_KITCHEN_TICKETS_STORE, LOCAL_KITCHEN_TICKET_OPERATIONS_STORE],
      "readwrite",
    );
    const ticketsStore = tx.objectStore(LOCAL_KITCHEN_TICKETS_STORE);
    const operationsStore = tx.objectStore(
      LOCAL_KITCHEN_TICKET_OPERATIONS_STORE,
    );
    const existing = await requestToPromise<
      LocalKitchenTicketRecord | undefined
    >(ticketsStore.get(localTicketId));
    if (!existing) return undefined;

    const now = new Date().toISOString();
    const updated: LocalKitchenTicketRecord = {
      ...existing,
      status,
      startedAt:
        status === "IN_PROGRESS"
          ? (existing.startedAt ?? now)
          : existing.startedAt,
      completedAt: status === "DONE" ? now : existing.completedAt,
      updatedAt: now,
    };
    ticketsStore.put(updated);
    appendLocalKitchenTicketOperation(operationsStore, {
      localTicketId,
      clientOrderId: existing.clientOrderId,
      branchId: existing.branchId,
      type: "status_changed",
      status,
      createdAt: now,
    });
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function listLocalKitchenTicketOperations(
  localTicketId: string,
): Promise<LocalKitchenTicketOperationRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      LOCAL_KITCHEN_TICKET_OPERATIONS_STORE,
      "readonly",
    );
    const all = await requestToPromise<LocalKitchenTicketOperationRecord[]>(
      tx.objectStore(LOCAL_KITCHEN_TICKET_OPERATIONS_STORE).getAll(),
    );
    return all
      .filter((record) => record.localTicketId === localTicketId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } finally {
    db.close();
  }
}

export async function listLocalOrdersBySyncStatus(
  shiftId: string,
  syncStatuses: LocalOrderRecord["syncStatus"][],
): Promise<LocalOrderRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_ORDERS_STORE, "readonly");
    const all = await requestToPromise<LocalOrderRecord[]>(
      tx.objectStore(LOCAL_ORDERS_STORE).getAll(),
    );
    return all
      .filter(
        (record) =>
          record.shiftId === shiftId &&
          syncStatuses.includes(record.syncStatus),
      )
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } finally {
    db.close();
  }
}

export async function updateLocalOrder(
  clientOrderId: string,
  updates: UpdateLocalOrderInput,
  options: { addedCartItems?: LocalOrderRecord["cartItems"] } = {},
): Promise<LocalOrderRecord | undefined> {
  const hasAddedItems = Boolean(
    options.addedCartItems && options.addedCartItems.length > 0,
  );
  const existingForRouting = hasAddedItems
    ? await getLocalOrder(clientOrderId)
    : undefined;
  const routingEntries = existingForRouting
    ? await getCachedKitchenRoutingEntries(existingForRouting.branchId)
    : [];
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [
        LOCAL_ORDERS_STORE,
        LOCAL_ORDER_OPERATIONS_STORE,
        LOCAL_KITCHEN_TICKETS_STORE,
        LOCAL_KITCHEN_TICKET_ITEMS_STORE,
        LOCAL_KITCHEN_TICKET_OPERATIONS_STORE,
      ],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const kitchenTickets = tx.objectStore(LOCAL_KITCHEN_TICKETS_STORE);
    const kitchenTicketItems = tx.objectStore(LOCAL_KITCHEN_TICKET_ITEMS_STORE);
    const kitchenTicketOperations = tx.objectStore(
      LOCAL_KITCHEN_TICKET_OPERATIONS_STORE,
    );
    const existing = await requestToPromise<LocalOrderRecord | undefined>(
      localOrders.get(clientOrderId),
    );
    if (!existing) return undefined;

    const now = new Date().toISOString();
    const updated: LocalOrderRecord = {
      ...existing,
      ...updates,
      updatedAt: now,
    };
    localOrders.put(updated);
    appendLocalOrderOperation(operations, {
      type: "update_order",
      order: updated,
      payload: updates,
      createdAt: now,
    });
    if (options.addedCartItems && options.addedCartItems.length > 0) {
      const existingTickets = await requestToPromise<
        LocalKitchenTicketRecord[]
      >(kitchenTickets.getAll());
      const ticketSequence =
        existingTickets.filter(
          (ticket) => ticket.clientOrderId === clientOrderId,
        ).length + 1;
      appendLocalKitchenTicketsForOrder(
        kitchenTickets,
        kitchenTicketItems,
        kitchenTicketOperations,
        updated,
        now,
        options.addedCartItems,
        ticketSequence,
        routingEntries,
      );
    }
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function completeLocalOrder(
  clientOrderId: string,
  input: CompleteLocalOrderInput,
): Promise<LocalOrderRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_ORDERS_STORE, LOCAL_ORDER_OPERATIONS_STORE],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const existing = await requestToPromise<LocalOrderRecord | undefined>(
      localOrders.get(clientOrderId),
    );
    if (!existing) return undefined;

    const now = new Date().toISOString();
    const completedAt = input.completedAt ?? now;
    const updated: LocalOrderRecord = {
      ...existing,
      status: "PAID",
      syncStatus: "queued",
      completedShiftId: input.completedShiftId ?? existing.completedShiftId,
      customerId: input.customerId ?? existing.customerId,
      customerSnapshot: input.customerSnapshot ?? existing.customerSnapshot,
      customerSyncStatus:
        input.customerSyncStatus ?? existing.customerSyncStatus,
      memberReceiptSnapshot:
        input.memberReceiptSnapshot ?? existing.memberReceiptSnapshot,
      payments: input.payments,
      totals: input.totals ?? existing.totals,
      voucherCodes: input.voucherCodes,
      paymentCouponCode: input.paymentCouponCode,
      couponOrderItemIds: input.couponOrderItemIds,
      paymentCouponCartIndices: input.paymentCouponCartIndices,
      paymentCouponApplications: input.paymentCouponApplications,
      couponSnapshots: input.couponSnapshots ?? existing.couponSnapshots,
      voucherSnapshots: input.voucherSnapshots ?? existing.voucherSnapshots,
      // Payment checkout is authoritative — do not keep stale reward fields from OPEN order.
      rewardSnapshot: input.rewardSnapshot,
      rewardId: input.rewardId,
      discountBreakdown: input.discountBreakdown ?? existing.discountBreakdown,
      pricingSource: input.pricingSource ?? existing.pricingSource,
      completedAt,
      updatedAt: now,
    };
    localOrders.put(updated);
    appendLocalOrderOperation(operations, {
      type: "complete_order",
      order: updated,
      payload: input,
      createdAt: now,
    });
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function voidLocalOrder(
  clientOrderId: string,
  input: string | VoidLocalOrderInput = { reason: "voided" },
): Promise<LocalOrderRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_ORDERS_STORE, LOCAL_ORDER_OPERATIONS_STORE],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const existing = await requestToPromise<LocalOrderRecord | undefined>(
      localOrders.get(clientOrderId),
    );
    if (!existing) return undefined;
    if (existing.status !== "OPEN") {
      throw new Error("Only OPEN local orders can be voided");
    }

    const now = new Date().toISOString();
    const reason =
      typeof input === "string" ? input : input.reason.trim() || "voided";
    const voidedAt = typeof input === "string" ? now : (input.voidedAt ?? now);
    const voidedBy = typeof input === "string" ? undefined : input.voidedBy;
    const updated: LocalOrderRecord = {
      ...existing,
      status: "VOID",
      syncStatus: "not_ready",
      voidedAt,
      voidReason: reason,
      voidedBy,
      updatedAt: now,
    };
    localOrders.put(updated);
    appendLocalOrderOperation(operations, {
      type: "void_order",
      order: updated,
      syncStatus: "queued",
      capturedAt: voidedAt,
      performedBy: voidedBy,
      payload: { reason, voidedAt, voidedBy },
      createdAt: now,
    });
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function refundLocalOrder(
  clientOrderId: string,
  input: RefundLocalOrderInput,
): Promise<LocalOrderRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_ORDERS_STORE, LOCAL_ORDER_OPERATIONS_STORE],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const existing = await requestToPromise<LocalOrderRecord | undefined>(
      localOrders.get(clientOrderId),
    );
    if (!existing) return undefined;
    if (existing.status !== "PAID" && existing.status !== "SYNCED") {
      throw new Error("Only paid local orders can be refunded");
    }

    const now = new Date().toISOString();
    const refundedAt = input.refundedAt ?? now;
    const previousRefundTotal = existing.refundTotal ?? 0;
    const remainingAmount = Math.max(
      0,
      existing.totals.grandTotal - previousRefundTotal,
    );
    const amount = input.amount ?? remainingAmount;
    if (amount <= 0 || amount > remainingAmount) {
      throw new Error("Invalid refund amount");
    }

    const refund = {
      refundId: createLocalId("local_refund"),
      amount,
      methodId: input.methodId,
      reason: input.reason.trim() || "refund",
      refundedAt,
      refundedBy: input.refundedBy,
      returnStock: input.returnStock ?? false,
    };
    const refunds = [...(existing.refunds ?? []), refund];
    const updated: LocalOrderRecord = {
      ...existing,
      refunds,
      refundTotal: refunds.reduce((sum, item) => sum + item.amount, 0),
      updatedAt: now,
    };
    localOrders.put(updated);
    appendLocalOrderOperation(operations, {
      type: "refund_order",
      order: updated,
      syncStatus: "queued",
      capturedAt: refundedAt,
      performedBy: input.refundedBy,
      payload: refund,
      createdAt: now,
    });
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function moveLocalOrderTable(
  clientOrderId: string,
  input: MoveLocalOrderTableInput,
): Promise<LocalOrderRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_ORDERS_STORE, LOCAL_ORDER_OPERATIONS_STORE],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const existing = await requestToPromise<LocalOrderRecord | undefined>(
      localOrders.get(clientOrderId),
    );
    if (!existing) return undefined;
    if (existing.status !== "OPEN") {
      throw new Error("Only OPEN local orders can be moved");
    }

    const now = new Date().toISOString();
    const movedAt = input.movedAt ?? now;
    const previousSelection = existing.selection;
    const nextSelection = {
      ...existing.selection,
      tableId: input.toTableId,
      tableName: input.toTableName,
    };
    const updated: LocalOrderRecord = {
      ...existing,
      selection: nextSelection,
      tableIds: input.toTableId ? [input.toTableId] : [],
      tableNames: input.toTableName ? [input.toTableName] : [],
      updatedAt: now,
    };
    localOrders.put(updated);
    appendLocalOrderOperation(operations, {
      type: "move_table",
      order: updated,
      syncStatus: "queued",
      capturedAt: movedAt,
      performedBy: input.movedBy,
      payload: {
        fromTableId: previousSelection.tableId,
        fromTableName: previousSelection.tableName,
        toTableId: input.toTableId,
        toTableName: input.toTableName,
        movedAt,
        movedBy: input.movedBy,
        reason: input.reason,
      },
      createdAt: now,
    });
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function mergeLocalOrders(
  input: MergeLocalOrdersInput,
): Promise<LocalOrderRecord | undefined> {
  const sourceClientOrderIds = Array.from(
    new Set(
      input.sourceClientOrderIds.filter(
        (clientOrderId) => clientOrderId !== input.targetClientOrderId,
      ),
    ),
  );
  if (sourceClientOrderIds.length === 0) {
    throw new Error("At least one source order is required");
  }

  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_ORDERS_STORE, LOCAL_ORDER_OPERATIONS_STORE],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const target = await requestToPromise<LocalOrderRecord | undefined>(
      localOrders.get(input.targetClientOrderId),
    );
    if (!target) return undefined;
    if (target.status !== "OPEN") {
      throw new Error("Target local order must be OPEN");
    }

    const sources: LocalOrderRecord[] = [];
    for (const sourceClientOrderId of sourceClientOrderIds) {
      const source = await requestToPromise<LocalOrderRecord | undefined>(
        localOrders.get(sourceClientOrderId),
      );
      if (!source) {
        throw new Error(`Source local order not found: ${sourceClientOrderId}`);
      }
      if (source.status !== "OPEN") {
        throw new Error("Source local orders must be OPEN");
      }
      if (
        source.branchId !== target.branchId ||
        source.shiftId !== target.shiftId
      ) {
        throw new Error(
          "Source and target orders must be in the same branch and shift",
        );
      }
      sources.push(source);
    }

    const now = new Date().toISOString();
    const mergedAt = input.mergedAt ?? now;
    const tableIds = Array.from(
      new Set(
        [target, ...sources]
          .map((order) => order.selection.tableId)
          .filter((tableId): tableId is string => Boolean(tableId)),
      ),
    );
    const tableNames = Array.from(
      new Set(
        [target, ...sources]
          .map((order) => order.selection.tableName)
          .filter((tableName): tableName is string => Boolean(tableName)),
      ),
    );
    const updatedTarget: LocalOrderRecord = {
      ...target,
      cartItems: [
        ...target.cartItems,
        ...sources.flatMap((source) => source.cartItems),
      ],
      totals: input.totals,
      tableIds,
      tableNames,
      updatedAt: now,
    };
    localOrders.put(updatedTarget);
    appendLocalOrderOperation(operations, {
      type: "merge_orders",
      order: updatedTarget,
      syncStatus: "queued",
      capturedAt: mergedAt,
      performedBy: input.mergedBy,
      payload: {
        targetClientOrderId: input.targetClientOrderId,
        sourceClientOrderIds,
        mergedAt,
        mergedBy: input.mergedBy,
        reason: input.reason,
        beforeTotals: {
          target: target.totals,
          sources: sources.map((source) => ({
            clientOrderId: source.clientOrderId,
            totals: source.totals,
          })),
        },
        afterTotals: input.totals,
      },
      createdAt: now,
    });

    for (const source of sources) {
      const mergedSource: LocalOrderRecord = {
        ...source,
        status: "MERGED",
        syncStatus: "not_ready",
        mergedIntoClientOrderId: target.clientOrderId,
        mergedAt,
        mergedBy: input.mergedBy,
        updatedAt: now,
      };
      localOrders.put(mergedSource);
      appendLocalOrderOperation(operations, {
        type: "merge_orders",
        order: mergedSource,
        syncStatus: "queued",
        capturedAt: mergedAt,
        performedBy: input.mergedBy,
        payload: {
          sourceClientOrderId: source.clientOrderId,
          targetClientOrderId: target.clientOrderId,
          mergedAt,
          mergedBy: input.mergedBy,
          reason: input.reason,
        },
        createdAt: now,
      });
    }

    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updatedTarget;
  } finally {
    db.close();
  }
}

export async function markLocalOrderSyncStatus(
  clientOrderId: string,
  updates: Partial<
    Pick<
      LocalOrderRecord,
      | "syncStatus"
      | "status"
      | "serverOrderId"
      | "lastSyncError"
      | "retryCount"
      | "updatedAt"
    >
  >,
): Promise<LocalOrderRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(
      [LOCAL_ORDERS_STORE, LOCAL_ORDER_OPERATIONS_STORE],
      "readwrite",
    );
    const localOrders = tx.objectStore(LOCAL_ORDERS_STORE);
    const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const existing = await requestToPromise<LocalOrderRecord | undefined>(
      localOrders.get(clientOrderId),
    );
    if (!existing) return undefined;

    const now = new Date().toISOString();
    const updated: LocalOrderRecord = {
      ...existing,
      ...updates,
      updatedAt: updates.updatedAt ?? now,
    };
    localOrders.put(updated);
    appendLocalOrderOperation(operations, {
      type:
        updates.syncStatus === "needs_review"
          ? "manual_resolved"
          : "sync_marked",
      order: updated,
      payload: updates,
      createdAt: now,
    });
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function listLocalOrderOperations(
  clientOrderId: string,
): Promise<LocalOrderOperationRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_ORDER_OPERATIONS_STORE, "readonly");
    const all = await requestToPromise<LocalOrderOperationRecord[]>(
      tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE).getAll(),
    );
    return all
      .filter((record) => record.clientOrderId === clientOrderId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } finally {
    db.close();
  }
}

export async function listLocalOrderOperationsForShift(
  shiftId: string,
  filters: {
    types?: LocalOrderOperationType[];
    syncStatuses?: NonNullable<LocalOrderOperationRecord["syncStatus"]>[];
  } = {},
): Promise<LocalOrderOperationRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_ORDER_OPERATIONS_STORE, "readonly");
    const all = await requestToPromise<LocalOrderOperationRecord[]>(
      tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE).getAll(),
    );
    return all
      .filter((record) => record.shiftId === shiftId)
      .filter((record) => !filters.types || filters.types.includes(record.type))
      .filter(
        (record) =>
          !filters.syncStatuses ||
          filters.syncStatuses.includes(record.syncStatus ?? "not_ready"),
      )
      .sort((a, b) =>
        (a.capturedAt ?? a.createdAt).localeCompare(
          b.capturedAt ?? b.createdAt,
        ),
      );
  } finally {
    db.close();
  }
}

export async function markLocalOrderOperationSyncStatus(
  operationId: string,
  updates: Partial<
    Pick<
      LocalOrderOperationRecord,
      "syncStatus" | "lastSyncError" | "retryCount" | "syncedAt"
    >
  >,
): Promise<LocalOrderOperationRecord | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_ORDER_OPERATIONS_STORE, "readwrite");
    const store = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
    const existing = await requestToPromise<
      LocalOrderOperationRecord | undefined
    >(
      store.index("by_operation_id").get(operationId) as IDBRequest<
        LocalOrderOperationRecord | undefined
      >,
    );
    if (!existing?.id) return undefined;

    const updated: LocalOrderOperationRecord = {
      ...existing,
      ...updates,
    };
    store.put(updated);
    await transactionDone(tx);
    notifyOfflineSyncChanged();
    return updated;
  } finally {
    db.close();
  }
}

export async function saveReferenceCache<TData>(
  record: ReferenceCacheRecord<TData>,
): Promise<void> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(REFERENCE_CACHE_STORE, "readwrite");
    tx.objectStore(REFERENCE_CACHE_STORE).put(record);
    await transactionDone(tx);
    notifyOfflineSyncChanged();
  } finally {
    db.close();
  }
}

export async function saveReferenceCacheBatch(
  records: ReferenceCacheRecord[],
): Promise<void> {
  if (records.length === 0) return;

  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(REFERENCE_CACHE_STORE, "readwrite");
    const store = tx.objectStore(REFERENCE_CACHE_STORE);
    for (const record of records) {
      store.put(record);
    }
    await transactionDone(tx);
    notifyOfflineSyncChanged();
  } finally {
    db.close();
  }
}

export async function getReferenceCache<TData = unknown>(
  key: string,
): Promise<ReferenceCacheRecord<TData> | undefined> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(REFERENCE_CACHE_STORE, "readonly");
    return await requestToPromise<ReferenceCacheRecord<TData> | undefined>(
      tx.objectStore(REFERENCE_CACHE_STORE).get(key),
    );
  } finally {
    db.close();
  }
}

export async function listReferenceCacheForBranch(
  branchId: string,
): Promise<ReferenceCacheRecord[]> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(REFERENCE_CACHE_STORE, "readonly");
    const all = await requestToPromise<ReferenceCacheRecord[]>(
      tx.objectStore(REFERENCE_CACHE_STORE).getAll(),
    );
    return all
      .filter((record) => record.branchId === branchId)
      .sort((a, b) => a.key.localeCompare(b.key));
  } finally {
    db.close();
  }
}

export async function getReferenceCacheStaleness(
  key: string,
  maxAgeMs: number,
): Promise<ReferenceCacheStaleness> {
  const record = await getReferenceCache(key);
  if (!record) {
    return { exists: false, stale: true, maxAgeMs };
  }

  const cachedAtMs = new Date(record.cachedAt).getTime();
  const ageMs = Number.isNaN(cachedAtMs) ? undefined : Date.now() - cachedAtMs;
  const expiredAtMs = record.expiresAt
    ? new Date(record.expiresAt).getTime()
    : undefined;
  const isExpired =
    expiredAtMs !== undefined &&
    !Number.isNaN(expiredAtMs) &&
    Date.now() > expiredAtMs;

  return {
    exists: true,
    stale: ageMs === undefined || ageMs > maxAgeMs || isExpired,
    cachedAt: record.cachedAt,
    ageMs,
    maxAgeMs,
  };
}

export async function putCompactedLocalOrder(
  record: LocalOrderRecord,
): Promise<void> {
  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(LOCAL_ORDERS_STORE, "readwrite");
    tx.objectStore(LOCAL_ORDERS_STORE).put(record);
    await transactionDone(tx);
    notifyOfflineSyncChanged();
  } finally {
    db.close();
  }
}

export async function purgeLocalOrderAuxiliaryData(
  clientOrderId: string,
  options: {
    deleteSyncedOperations?: boolean;
    deleteKitchenTickets?: boolean;
  } = {},
): Promise<{ operationsDeleted: number; kitchenTicketsDeleted: number }> {
  const deleteSyncedOperations = options.deleteSyncedOperations ?? true;
  const deleteKitchenTickets = options.deleteKitchenTickets ?? true;
  const stores: string[] = [];
  if (deleteSyncedOperations) stores.push(LOCAL_ORDER_OPERATIONS_STORE);
  if (deleteKitchenTickets) {
    stores.push(
      LOCAL_KITCHEN_TICKETS_STORE,
      LOCAL_KITCHEN_TICKET_ITEMS_STORE,
      LOCAL_KITCHEN_TICKET_OPERATIONS_STORE,
    );
  }
  if (stores.length === 0) {
    return { operationsDeleted: 0, kitchenTicketsDeleted: 0 };
  }

  const db = await openOfflineSyncDb();
  try {
    const tx = db.transaction(stores, "readwrite");
    let operationsDeleted = 0;
    let kitchenTicketsDeleted = 0;

    if (deleteSyncedOperations) {
      const operations = tx.objectStore(LOCAL_ORDER_OPERATIONS_STORE);
      const rows = await requestToPromise<LocalOrderOperationRecord[]>(
        operations.getAll(),
      );
      for (const row of rows) {
        if (
          row.clientOrderId !== clientOrderId ||
          row.syncStatus !== "synced" ||
          row.id == null
        ) {
          continue;
        }
        operations.delete(row.id);
        operationsDeleted += 1;
      }
    }

    if (deleteKitchenTickets) {
      const tickets = tx.objectStore(LOCAL_KITCHEN_TICKETS_STORE);
      const items = tx.objectStore(LOCAL_KITCHEN_TICKET_ITEMS_STORE);
      const ticketOps = tx.objectStore(LOCAL_KITCHEN_TICKET_OPERATIONS_STORE);

      const ticketRows = await requestToPromise<LocalKitchenTicketRecord[]>(
        tickets.getAll(),
      );
      const ticketIds = ticketRows
        .filter((ticket) => ticket.clientOrderId === clientOrderId)
        .map((ticket) => ticket.localTicketId);

      for (const localTicketId of ticketIds) {
        tickets.delete(localTicketId);
        kitchenTicketsDeleted += 1;
      }

      const itemRows = await requestToPromise<LocalKitchenTicketItemRecord[]>(
        items.getAll(),
      );
      for (const item of itemRows) {
        if (item.clientOrderId === clientOrderId) {
          items.delete(item.localTicketItemId);
        }
      }

      const opRows = await requestToPromise<
        LocalKitchenTicketOperationRecord[]
      >(ticketOps.getAll());
      for (const op of opRows) {
        if (op.clientOrderId === clientOrderId && op.id != null) {
          ticketOps.delete(op.id);
        }
      }
    }

    await transactionDone(tx);
    if (operationsDeleted > 0 || kitchenTicketsDeleted > 0) {
      notifyOfflineSyncChanged();
    }
    return { operationsDeleted, kitchenTicketsDeleted };
  } finally {
    db.close();
  }
}
